import { Box, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { TaskWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { useServiceRequests, useTasks } from 'src/careflow/ph-core/fhir-client/fhir-react';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import useHealthCareServicesByServiceRequests from 'src/hooks/useHealthCareServicesByServiceRequests';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { Task } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/Task';
import {
  filterTasksByDefinition,
  getAssessmentFromTask,
  getAssessmentUrlWithUserId,
  isAssessmentReadyToStart,
} from 'src/careflow/helpers/common';
import { TaskPermissions } from '../../helpers/types';
import AssessmentFrame from './assessment/frame/AssessmentFrame';
import { ServiceRequestWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { useQuery } from '@tanstack/react-query';
import { getRegistries } from 'src/careflow/ph-core/multi-tenant/registry/registry';
import { CREATE_TASK, UPDATE_TASK, useEventBus } from 'src/event-bus';
import { commonMenuTaskHandlers } from 'src/careflow/care-coordination/task/common/handlers';
import TasksByPathway from 'src/careflow/care-coordination/task/patient/TasksByPathway';

export interface PatientTasksProps {
  patient: WrappedPatient;
  includeSubTasks?: boolean;
}

const PatientTasks = ({ patient, includeSubTasks }: PatientTasksProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const authUser = useAuth().getCurrentUser();

  const [tasks, { isLoading: isTasksLoading, refresh: refreshTaskList }] = useTasks({
    filter: {
      subject: patient?.id,
    },
    map: TaskWrapper,
  });

  const [ serviceRequest, { refresh: refreshServiceRequest} ] = useServiceRequests({
    filter: { subject: patient?.id },
    map: ServiceRequestWrapper,
    autofetch: !!patient?.id
  });

  const { onEvent, removeEvent } = useEventBus();

  const [openAssessmentFrame, setOpenAssessmentFrame] = useState(false);
  const [selectedTaskAssessmentId, setSelectedTaskAssessmentId] = useState<null | string>(null);
  const [selectedTaskAssessmentUrl, setSelectedTaskAssessmentUrl] = useState<null | string>(null);

  const onCancel = () => {
    setOpenAssessmentFrame(false);
    setSelectedTaskAssessmentId(null);
    setSelectedTaskAssessmentUrl(null);
  };

  const selectedTaskAssessment = useMemo(
    () => tasks.find((t) => t?.id === selectedTaskAssessmentId),
    [tasks, selectedTaskAssessmentId]
  );

  const taskPermissions: TaskPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.TASKS.ADD] }),
      isAllowedToEdit: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.TASKS.EDIT] }),
      notes: {
        isAllowedToView: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.TASKS.NOTES.VIEW] }),
        isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.TASKS.NOTES.ADD] }),
      },
      isAllowedToAssignProgram: checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.TASKS.HANDLER.ASSIGN_PROGRAM],
      }),
      isAllowedReferralInternal: checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.TASKS.HANDLER.REFERRAL_INTERNAL],
      }),
      isAllowedReferralExternal:checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.TASKS.HANDLER.REFERRAL_EXTERNAL],
      }),
      isAllowedAppointmentScheduling: checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.TASKS.HANDLER.APPOINTMENT],
      }),
      isAllowedWarmHandoff: checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.TASKS.HANDLER.WARM_HANDOFF],
      }),
    }),
    []
  );

  useEffect(() => {
    if (selectedTaskAssessment) {
      openOrUpdateAssessment();
    }
  }, [selectedTaskAssessment, tasks]);

  const { healthCareServices, isLoading: isHealthCareServicesLoading } =
    useHealthCareServicesByServiceRequests(null, patient?.id);

  const isLoading = isTasksLoading || isHealthCareServicesLoading || !patient?.id;

  const handleAssessmentSelect = async (task: Task) => {
    if (!task.id) return;

    const inputAssessment = getAssessmentFromTask(task);
    if (!inputAssessment) return;

    if (isAssessmentReadyToStart(inputAssessment)) {
      setSelectedTaskAssessmentId(task.id);
    } else {
      await refreshTaskList();
      setSelectedTaskAssessmentId(task.id);
    }
  };

  const openOrUpdateAssessment = () => {
    if (!selectedTaskAssessment) return;

    const inputAssessment = getAssessmentFromTask(selectedTaskAssessment);
    if (!inputAssessment) return;

    if (isAssessmentReadyToStart(inputAssessment)) {
      console.log('DEBUG isAssessmentReadyToStart');
      const assessmentUrl = getAssessmentUrlWithUserId(
        inputAssessment,
        authUser.user_fhir_uri,
        { taskId: selectedTaskAssessment?.id, patientId: patient?.id }
      );
      setSelectedTaskAssessmentUrl(assessmentUrl ?? null);
      setOpenAssessmentFrame(true);
    } else {
      enqueueSnackbar('Preparing assessment. Please try again later.', {
        variant: 'error',
      });
    }
    setSelectedTaskAssessmentId(null);
  };

  const refreshEntities = useCallback(() => {
    refreshTaskList();
    refreshServiceRequest();
  }, []);

  const { data: taskADRegistry } = useQuery({
    queryKey: ['taskActivityDefinition'],
    queryFn: async () => {
      const taskActivityDefinition = await getRegistries(undefined, 'task-activity-definition');
      return taskActivityDefinition;
    },
  });

  const taskActivityDefinition = useMemo(() => 
    taskADRegistry?.[0]?.keyValue ? JSON.parse(taskADRegistry?.[0]?.keyValue) : null
    ,[taskADRegistry]);

  const taskFilterByActivityDefinition = useMemo(() => {
    return filterTasksByDefinition(tasks, taskActivityDefinition?.patient);
  }, [tasks, taskActivityDefinition]);

  useEffect(() => {
    onEvent(CREATE_TASK, refreshEntities);
    onEvent(UPDATE_TASK, refreshEntities);
    return () => {
      removeEvent(CREATE_TASK, refreshEntities);
      removeEvent(UPDATE_TASK, refreshEntities);
    };
  }, []);

  return (
    <>
      {!isLoading ? (
        <Box mt={4}>
          <TasksByPathway
            permissions={taskPermissions}
            taskActivityDefinitionContext={taskActivityDefinition?.patient}
            tasks={taskFilterByActivityDefinition}
            patient={patient as WrappedPatient}
            healthCareServices={healthCareServices}
            serviceRequests={serviceRequest}
            menuTaskHandlers={commonMenuTaskHandlers}
            onAssessmentSelect={handleAssessmentSelect}
            openFrom='patient_details'
            includeSubTasks={includeSubTasks}
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
          <CircularProgress size={60} />
        </Box>
      )}
      {selectedTaskAssessmentUrl && openAssessmentFrame ? (
        <AssessmentFrame
          open={openAssessmentFrame}
          url={selectedTaskAssessmentUrl}
          onCancel={onCancel}
        />
      ) : null}
    </>
  );
};

export default PatientTasks;
